import { LoaderFunction, useLoaderData } from "react-router-dom";
import {
  Book,
  ButtonLink,
  ButtonLinkType,
  Course,
  CourseDetails,
  getCourseDetails,
  getShippingAddresses,
  getTerm,
  getTerms,
  getUnmatchedCourses,
  MaterialFormat,
  OrderInfo,
  ShippingAddress,
  Term,
  TermIdentifier,
} from "common/api/decimal/methods/course";
import { DecimalResult } from "../../../../common/api/decimal/utils.ts";
import { getActiveAssets } from "common/api/decimal/methods/license";
import { canBeOpenedInReader } from "common/features/asset/assetHelpers.ts";
import { loadBrandOnce } from "../../../root/rootLoader.ts";
import { getCoursesExtras } from "routes/home/homeContext.ts";
import {
  CompareCart,
  getCart,
} from "common/api/decimal/methods/compareAndPurchase.ts";
import {
  kindToMaterialFormat,
  resolveAlternateAccessVbid,
} from "./common/materialStoreProductUtils.ts";
import {
  addStorePricing,
  StorePricing,
} from "../../common/product/mini-pdp/storeLoaderUtils.ts";
import { once } from "common/utils/function.ts";
import buildProgramDetails, {
  ProgramDetailsType,
} from "routes/home/common/courses/buildProgramDetails.ts";
import { useMemo } from "react";
import { buildNoCoursesResponse } from "../noCoursesResponse.ts";

export const UNMATCHED_TERM_ID = "unmatched";

export type TermLite = Omit<
  Term,
  | "courses"
  | "institutionProgram"
  | "programDecision"
  | "inclusiveAccessExtras"
  | "noProgramExtras"
>;

export interface CourseData {
  courses: ExpandedCourse[];
  materialCount: number;
  allMaterials: CourseMaterial[];
  currentTerm: TermLite | undefined;
  terms: TermIdentifier[];
  compareCart?: CompareCart;
  shippingAddresses?: ShippingAddress[];
  learnerRequiredMaterials: CourseMaterial[];
}

export interface AllCourseData extends CourseData {
  programDetails: ProgramDetailsType;
}

const CONTENT_LAUNCH_TYPES: ButtonLinkType[] = [
  "launch",
  "launch_book",
  "open_book",
];

export type ExpandedMaterialFormat =
  | MaterialFormat
  | "supplies"
  | "print_bundle";

export type CourseMaterial = Omit<Book, "materialFormat"> &
  StorePricing & {
    courseId: number | string;
    materialFormat: ExpandedMaterialFormat;
    orderInfo?: OrderInfo;
    // set when an ePOS link was traded for an open_book link, so the
    // diagnostics dialog can explain why the response and the buttons differ
    alternateAccessVbid?: string;
  };

export type ExpandedCourse = Omit<CourseDetails, "books"> & {
  courseMaterials: CourseMaterial[];
};

export function useCoursesLoaderData(): CourseData {
  const all = useLoaderData<AllCourseData>();
  return useMemo(() => {
    const { programDetails: _, ...rest } = all;
    return rest;
  }, [all]);
}

export function useProgramDetails(): ProgramDetailsType {
  const courseData = useLoaderData<AllCourseData>();
  return useMemo(() => courseData.programDetails, [courseData.programDetails]);
}

export const loadTermsOnce = once(async () => {
  const result = await getTerms();
  if (!result.ok && result.status === 404) {
    throw await buildNoCoursesResponse("No Terms Found");
  }
  return result.data ?? [];
});

const buildCourseAndMaterialData = (courses: CourseDetails[]) => {
  const allMaterials: CourseMaterial[] = [];
  const expandedCourses = courses.reduce(
    (final: ExpandedCourse[], courseDetails: CourseDetails) => {
      const courseMaterials =
        courseDetails.books?.map((book: Book) => ({
          ...book,
          courseId: courseDetails.id,
        })) ?? [];
      allMaterials.push(...courseMaterials);
      const { books: _, ...rest } = courseDetails;
      final.push({
        ...rest,
        courseMaterials,
      });
      return final;
    },
    []
  );
  return [expandedCourses, allMaterials] as const;
};

const fetchCompareCart = (term: Term | undefined) => {
  if (!term?.hasCompareCart) {
    return Promise.resolve(undefined);
  }
  return getCart().then(({ data }) => data);
};

const fetchCourses = (
  termId: string | number,
  term: Term | undefined
): Promise<CourseDetails[]> => {
  if (termId === UNMATCHED_TERM_ID) {
    return getUnmatchedCourses().then((result) => result?.data ?? []);
  }
  if (!term) {
    throw new Response("Not Found", {
      status: 404,
      statusText: `No term data found for Courses call`,
    });
  }
  const courseDetailPromises: Promise<DecimalResult<CourseDetails>>[] =
    term.courses.map((course: Course) => getCourseDetails(course.id, termId));

  return Promise.all(courseDetailPromises).then((result) => {
    return result
      .filter(
        (r): r is DecimalResult<CourseDetails> & { data: CourseDetails } =>
          r.ok && !!r.data
      )
      .map(({ data }) => data);
  });
};

const fetchTerm = (termId: string | number): Promise<Term | undefined> => {
  if (termId === UNMATCHED_TERM_ID) {
    return Promise.resolve(undefined);
  }
  return getTerm(termId).then((result) => {
    if (!result?.data) {
      throw new Response("Not Found", {
        status: 404,
        statusText: `No data found for Term with id of ${termId}`,
      });
    }
    return result.data;
  });
};

const fetchUserShippingAddresses = async (): Promise<ShippingAddress[]> => {
  const brand = await loadBrandOnce();
  if (
    brand.VirtualStore.companyId &&
    brand.VirtualStore.EA.enableShippingAddressChanges
  ) {
    const result = await getShippingAddresses();
    if (result.ok && result.data) {
      return result.data;
    }
  }
  return [];
};

const enhanceForVirtualStore = ({
  programDetails,
  terms,
  currentTerm,
  courses,
  allMaterials,
  learnerRequiredMaterials,
}: {
  programDetails: ProgramDetailsType;
  terms: TermIdentifier[];
  currentTerm: TermLite | undefined;
  courses: ExpandedCourse[];
  allMaterials: CourseMaterial[];
  learnerRequiredMaterials: CourseMaterial[];
}): Promise<AllCourseData> => {
  const sortedCourses = courses.sort(
    (a, b) =>
      // Number() converts boolean to 1 (true) or 0 (false) - courses with books come first
      Number((b.courseMaterials?.length ?? 0) > 0) -
      Number((a.courseMaterials?.length ?? 0) > 0)
  );
  return Promise.all([
    fetchUserShippingAddresses(),
    addStorePricing(allMaterials, {
      isPriceNeeded: (material) =>
        material.buttonLinks.some((link) => link.type === "ePOS") ||
        material.purchaseLinks.some(
          (link) => link.type === "opt_in" || link.type === "opt_out"
        ),
      adjuster: (material, _product, adoptedProduct) => {
        if (adoptedProduct) {
          material.materialFormat = kindToMaterialFormat(adoptedProduct.kind);
        }
      },
    }),
    getActiveAssets(1).then((result) => result.data ?? []),
  ]).then(([shippingAddresses, , activeAssets]) => {
    const openableVbids = new Set(
      activeAssets
        .filter(({ manageAsset, license }) =>
          canBeOpenedInReader(manageAsset, license)
        )
        .map(({ manageAsset }) => manageAsset.vbid)
    );
    // Decimal's buttonLinks only cover the adopted vbid, so a student who
    // separately purchased the digital format of a material gets no access
    // link. Trade the purchase prompt for the digital vbid's reader link
    // when they hold a license for it.
    if (openableVbids.size > 0) {
      allMaterials.forEach((material) => {
        const hasPurchasePrompt = material.buttonLinks.some(
          (link) => link.type === "ePOS"
        );
        const hasContentLaunch = material.buttonLinks.some((link) =>
          CONTENT_LAUNCH_TYPES.includes(link.type)
        );
        if (!hasPurchasePrompt || hasContentLaunch) {
          return;
        }
        const alternateAccessVbid = resolveAlternateAccessVbid(
          material,
          openableVbids
        );
        if (!alternateAccessVbid) {
          return;
        }
        material.alternateAccessVbid = alternateAccessVbid;
        material.buttonLinks = material.buttonLinks.map(
          (buttonLink): ButtonLink =>
            buttonLink.type === "ePOS"
              ? { type: "open_book", link: alternateAccessVbid }
              : buttonLink
        );
      });
    }
    return {
      programDetails,
      terms,
      currentTerm,
      courses: sortedCourses,
      materialCount: allMaterials.length,
      allMaterials,
      learnerRequiredMaterials,
      shippingAddresses,
      compareCart: undefined,
    };
  });
};

const getTermId = (
  request: Request,
  defaultTermId: string | number
): string | number => {
  const searchParams = new URL(request.url).searchParams;
  const termIdParam = searchParams.get("termId");
  const termId = termIdParam ?? defaultTermId;
  if (!termId) {
    throw new Response("Not Found", {
      status: 404,
      statusText: "No Term data to show.",
    });
  }
  return termId;
};

// PVS pricing reflects what the user will be charged as a student, so courses
// where they are an instructor must not contribute to the totals. A dual-role
// user is a learner in some courses and an instructor in others.
const getLearnerRequiredMaterials = (courses: ExpandedCourse[]) => {
  return courses.reduce((materials, { userRole, courseMaterials }) => {
    if (userRole === "learner") {
      materials.push(
        ...courseMaterials.filter(
          (material) => material.requiredCode === "required"
        )
      );
    }
    return materials;
  }, [] as CourseMaterial[]);
};

export const loadCourses: LoaderFunction = ({
  request,
}): Promise<AllCourseData> => {
  return Promise.all([
    loadTermsOnce(),
    getCoursesExtras(),
    loadBrandOnce(),
  ]).then(([terms, { defaultTermId, brand }, fullBrand]) => {
    const termId = getTermId(request, defaultTermId);
    return fetchTerm(termId).then((currentTerm) => {
      return Promise.all([
        fetchCourses(termId, currentTerm),
        fetchCompareCart(currentTerm),
      ]).then(([courses, compareCart]) => {
        const isVirtualStore = !!fullBrand.VirtualStore?.companyId;
        const [expandedCourses, allMaterials] =
          buildCourseAndMaterialData(courses);
        const learnerRequiredMaterials =
          getLearnerRequiredMaterials(expandedCourses);

        const optableItems = allMaterials.filter(
          (material) =>
            material.materialStatus === "opted_in" ||
            material.materialStatus === "opted_out"
        );

        const totalOptableItems = optableItems.length;

        if (
          !totalOptableItems &&
          currentTerm?.institutionProgram === "inclusive_access"
        ) {
          currentTerm.institutionProgram = null;
          currentTerm.programDecision = "no_program";
        }

        const programDetails = buildProgramDetails({
          brand,
          term: currentTerm!,
        });

        if (!isVirtualStore) {
          return {
            programDetails,
            terms,
            currentTerm,
            courses: expandedCourses,
            materialCount: allMaterials.length,
            allMaterials,
            learnerRequiredMaterials,
            compareCart,
            shippingAddresses: [],
          };
        }
        return enhanceForVirtualStore({
          programDetails,
          terms,
          currentTerm,
          courses: expandedCourses,
          allMaterials,
          learnerRequiredMaterials,
        });
      });
    });
  });
};
